import React, { useState, useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchKeywords,
  deleteKeyword,
  listOfAnalysts,
} from "../services/NewsService";

// Table headers for keywords management
const TABLE_HEADERS = [
  "tag",
  "target_ticker",
  "buyer_ticker",
  "company_keywords",
  "special_queries",
  "analysts",
  "status",
];

// Mobile-only headers for responsive design
const MOBILE_HEADERS = ["tag", "target_ticker", "buyer_ticker", "status"];

const KeywordsManagement = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [keywordsData, setKeywordsData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [analystFilter, setAnalystFilter] = useState("");
  const [analysts, setAnalysts] = useState([]);
  const [analystsLoading, setAnalystsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Track screen size for responsive table
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Handle navigation state from keyword management pages
  useEffect(() => {
    if (location.state?.filterValue) {
      setGlobalFilter(location.state.filterValue);
    }
  }, [location.state]);

  // Load analysts list
  useEffect(() => {
    const loadAnalysts = async () => {
      setAnalystsLoading(true);
      try {
        const data = await listOfAnalysts();
        const analystsList = data.analysts || [];
        setAnalysts(analystsList);
      } catch (err) {
        console.error("Error loading analysts:", err);
      } finally {
        setAnalystsLoading(false);
      }
    };

    loadAnalysts();
  }, []);

  // Load keywords data from API (load all, filter on frontend)
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        // Load all keywords (no analyst filter on backend)
        const data = await fetchKeywords();
        if (isMounted) {
          setKeywordsData(data);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching keywords data:", error);
          setError("Failed to fetch keywords data. Please try again.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteKeyword(selectedRow.id);
      const updatedData = await fetchKeywords();
      setKeywordsData(updatedData || []);
    } catch (error) {
      console.error("Error deleting keyword:", error);
      setError("Failed to delete keyword. Please try again.");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedRow(null);
    }
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return keywordsData;
    return [...keywordsData].sort((a, b) => {
      // Map field names - analysts might be stored as "users" in the data
      const aField =
        sortConfig.key === "analysts"
          ? a.users
            ? "users"
            : "analysts"
          : sortConfig.key;
      const bField =
        sortConfig.key === "analysts"
          ? b.users
            ? "users"
            : "analysts"
          : sortConfig.key;

      let aVal = a[aField] || a[sortConfig.key];
      let bVal = b[bField] || b[sortConfig.key];

      // Handle arrays (e.g., company_keywords, special_queries, analysts)
      if (Array.isArray(aVal)) {
        aVal = aVal.join(", ");
      }
      if (Array.isArray(bVal)) {
        bVal = bVal.join(", ");
      }

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortConfig.direction === "asc" ? -1 : 1;
      if (bVal == null) return sortConfig.direction === "asc" ? 1 : -1;

      // Handle numeric values
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      // Handle string values
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [keywordsData, sortConfig]);

  const filteredData = useMemo(() => {
    let filtered = sortedData;

    // Apply analyst filter on frontend
    if (analystFilter) {
      const analystName =
        typeof analystFilter === "object" ? analystFilter.name : analystFilter;

      filtered = filtered.filter((keyword) => {
        const users = keyword.users || "";
        // Check if users field contains the analyst name (case-insensitive)
        return users.toLowerCase().includes(analystName.toLowerCase());
      });
    }

    // Apply status filter
    if (statusFilter !== "All") {
      filtered = filtered.filter((item) => item.status === statusFilter);
    }

    // Apply search filter - search all fields including new columns
    if (globalFilter) {
      const searchTerm = globalFilter.toLowerCase();
      filtered = filtered.filter((item) => {
        // Helper function to convert any value to searchable string
        const valueToString = (val) => {
          if (val == null) return "";
          if (Array.isArray(val)) {
            return val.join(", ").toLowerCase();
          }
          if (typeof val === "object") {
            return JSON.stringify(val).toLowerCase();
          }
          return String(val).toLowerCase();
        };

        // Search in all standard fields
        const searchableFields = [
          item.tag,
          item.language,
          item.target_ticker,
          item.buyer_ticker,
          item.status,
          item.company_keywords,
          item.special_queries,
          item.analysts,
          item.users, // Also search users field (backend field name for analysts)
          item.generic_keywords, // Legacy field name
        ];

        // Check if search term matches any field
        return searchableFields.some((field) => {
          const fieldStr = valueToString(field);
          return fieldStr.includes(searchTerm);
        });
      });
    }

    return filtered;
  }, [sortedData, globalFilter, statusFilter, analystFilter]);

  // Calculate active tags count
  const activeTagsCount = useMemo(() => {
    return keywordsData.filter((item) => item.status === "Active").length;
  }, [keywordsData]);

  const formatFieldValue = (value, field) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";

    // Special formatting for status field
    if (field === "status") {
      return (
        <Chip
          label={value}
          color={
            value === "Active"
              ? "success"
              : value === "Inactive"
              ? "error"
              : "default"
          }
          size="small"
          variant="outlined"
        />
      );
    }

    // Handle analysts/users field - can be string (comma-separated) or array
    if (field === "analysts" || field === "users") {
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      return String(value || "");
    }

    // Handle special_queries - can be array or string
    if (field === "special_queries") {
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      return String(value || "");
    }

    // Handle arrays (e.g., company_keywords)
    if (Array.isArray(value)) {
      return value.join(", ");
    }

    return String(value);
  };

  const getFieldLabel = (field) => {
    const labelMap = {
      tag: "Tag",
      target_ticker: "Target Ticker",
      buyer_ticker: "Buyer Ticker",
      status: "Status",
      company_keywords: "Company Keywords",
      analysts: "Analysts",
      special_queries: "Special Queries",
      users: "Analysts",
    };
    return (
      labelMap[field] ||
      field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Typography variant="h2" fontWeight="bold" mb={1}>
        Keywords Management
      </Typography>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: theme.palette.background.paper,
          p: { xs: 1, sm: 2 },
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          mx: { xs: 1, sm: 0 },
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate("/Create_Keyword")}
          aria-label="Create new keyword"
          sx={{
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          Create
        </Button>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.background.default,
              },
            }}
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
            <MenuItem value="All">All</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Analyst</InputLabel>
          <Select
            value={analystFilter}
            onChange={(e) => setAnalystFilter(e.target.value)}
            label="Analyst"
            disabled={analystsLoading}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.background.default,
              },
            }}
          >
            <MenuItem value="">All Analysts</MenuItem>
            {analysts.map((analyst) => {
              const analystName =
                typeof analyst === "object" ? analyst.name : analyst;
              const analystValue =
                typeof analyst === "object" ? analyst : analyst;
              const analystKey =
                typeof analyst === "object"
                  ? analyst.id || analyst.name
                  : analyst;
              return (
                <MenuItem key={analystKey} value={analystValue}>
                  {analystName}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          sx={{
            minWidth: { xs: 150, sm: 200 },
            maxWidth: { xs: "100%", sm: 300 },
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.default,
            },
          }}
          aria-label="Search keywords"
        />

        <Typography
          variant="body2"
          sx={{
            ml: 1,
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          Active Tags: {activeTagsCount}
        </Typography>
      </Box>

      {/* Table section */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          mx: { xs: 1, sm: 0 },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            width: "100%",
            maxWidth: "100%",
            height: "100%",
            overflowY: "auto",
            overflowX: "auto",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress aria-label="Loading keywords" />
            </Box>
          ) : (
            <Table
              size="small"
              stickyHeader
              aria-label="Keywords Management Table"
              sx={{
                fontSize: "0.75rem",
                "& .MuiTableCell-root": {
                  fontSize: "0.75rem",
                  padding: { xs: "2px 4px", sm: "4px 6px" },
                  borderRight: `1px solid ${theme.palette.divider}`,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: { xs: "80px", sm: "auto" },
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  backgroundColor: theme.palette.grey[100],
                  fontWeight: 600,
                  fontSize: { xs: "0.78rem", sm: "0.84rem" },
                  padding: { xs: "4px 6px", sm: "6px 8px" },
                },
                "& .MuiTableRow-root:hover": {
                  backgroundColor: theme.palette.grey[400],
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {/* Show all headers on desktop, only mobile headers on mobile */}
                  {(isMobile ? MOBILE_HEADERS : TABLE_HEADERS).map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        py: 0.5,
                        px: 1,
                        display: {
                          xs: MOBILE_HEADERS.includes(header)
                            ? "table-cell"
                            : "none",
                          lg: "table-cell",
                        },
                      }}
                    >
                      <TableSortLabel
                        active={sortConfig.key === header}
                        direction={
                          sortConfig.key === header
                            ? sortConfig.direction
                            : "asc"
                        }
                        onClick={() => handleSort(header)}
                        aria-label={`Sort by ${getFieldLabel(header)}`}
                        sx={{ fontSize: "0.7rem" }}
                      >
                        {getFieldLabel(header)}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{ py: 0.5, px: 1 }}
                    aria-label="Actions"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData?.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        (isMobile
                          ? MOBILE_HEADERS.length
                          : TABLE_HEADERS.length) + 1
                      }
                      align="center"
                    >
                      {loading ? "Loading..." : "No keywords found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData?.map((row) => (
                    <TableRow key={row.id} hover>
                      {TABLE_HEADERS.map((field) => {
                        // Map field names - analysts might be stored as "users" in the data
                        const dataField =
                          field === "analysts"
                            ? row.users
                              ? "users"
                              : "analysts"
                            : field;
                        const cellValue = row[dataField] || row[field] || "";

                        return (
                          <TableCell
                            key={field}
                            sx={{
                              py: { xs: 0.25, sm: 0.5 },
                              px: { xs: 0.5, sm: 1 },
                              display: {
                                xs: MOBILE_HEADERS.includes(field)
                                  ? "table-cell"
                                  : "none",
                                lg: "table-cell",
                              },
                              fontSize: { xs: "0.7rem", sm: "0.75rem" },
                              // Make tag, language, and tickers same height (more compact)
                              minHeight: [
                                "tag",
                                "language",
                                "target_ticker",
                                "buyer_ticker",
                              ].includes(field)
                                ? "36px"
                                : "auto",
                              maxHeight: [
                                "tag",
                                "language",
                                "target_ticker",
                                "buyer_ticker",
                              ].includes(field)
                                ? "36px"
                                : "none",
                              verticalAlign: "middle",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={
                              typeof formatFieldValue(cellValue, field) ===
                              "string"
                                ? formatFieldValue(cellValue, field)
                                : String(cellValue)
                            }
                          >
                            {formatFieldValue(cellValue, field)}
                          </TableCell>
                        );
                      })}
                      <TableCell
                        align="center"
                        sx={{
                          py: { xs: 0.25, sm: 0.5 },
                          px: { xs: 0.5, sm: 1 },
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          gap={{ xs: 0.25, sm: 0.5 }}
                        >
                          <IconButton
                            onClick={() => navigate(`/Edit_Keyword/${row.id}`)}
                            size="small"
                            aria-label={`Edit keyword ${row.tag}`}
                            sx={{
                              padding: { xs: "1px", sm: "2px" },
                              minWidth: { xs: "24px", sm: "auto" },
                              height: { xs: "24px", sm: "auto" },
                            }}
                          >
                            <EditIcon
                              sx={{ fontSize: { xs: "0.8rem", sm: "1rem" } }}
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => navigate(`/Copy_Keyword/${row.id}`)}
                            size="small"
                            color="primary"
                            aria-label={`Copy keyword ${row.tag}`}
                            sx={{
                              padding: { xs: "1px", sm: "2px" },
                              minWidth: { xs: "24px", sm: "auto" },
                              height: { xs: "24px", sm: "auto" },
                            }}
                          >
                            <CopyIcon
                              sx={{ fontSize: { xs: "0.8rem", sm: "1rem" } }}
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(row)}
                            size="small"
                            sx={{
                              color: theme.palette.error.main,
                              padding: { xs: "1px", sm: "2px" },
                              minWidth: { xs: "24px", sm: "auto" },
                              height: { xs: "24px", sm: "auto" },
                            }}
                            aria-label={`Delete keyword ${row.id}`}
                          >
                            <DeleteIcon
                              sx={{ fontSize: { xs: "0.8rem", sm: "1rem" } }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this keyword entry?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            aria-label="Cancel delete"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            aria-label="Confirm delete"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KeywordsManagement;
